import * as React from 'react';
import Dropzone, { DropzoneState } from 'react-dropzone';
import styled from 'styled-components';
import { store } from '../../data/providers/FileUpload';
import { Icon } from './Icon';
import { Image } from './Image';
import { DotLoading } from './loading/DotLoading';

interface IProps {
  onChange: (f: { url: string }) => void;
  onDelete: (f: { url: string }) => void;
  path: string;
}

const InlineUploader = (props: IProps): React.ReactElement => {
  const [error, setError] = React.useState<boolean>(false);
  const [previewPath, setPreviewpath] = React.useState<string>('');
  const [hover, setHover] = React.useState<boolean>(false);
  const [dialog, setDialog] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);

  const onError = (): void => {
    setError(true);
    setDialog(false);
  };

  const openedDialog = (): void => {
    setDialog(true);
  };

  const onSuccess = (res: { url: string }): void => {
    setError(false);
    setDialog(false);
    setSaving(false);
    props.onChange(res);
    setPreviewpath('');
  };

  const onHover = (): void => {
    setHover(true);
  };

  const offHover = (): void => {
    setHover(false);
  };

  const onFile = (acceptedFiles: File[], rejectedFiles: File[]): void => {
    if (rejectedFiles.length > 0) {
      setError(true);
      return;
    }
    setError(false);
    setSaving(true);
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>): void => {
      setPreviewpath(e.target.result.toString());
    };
    reader.readAsDataURL(file);
    store(file, onSuccess, onError);
  };

  return (
    <div>
      <div onMouseEnter={onHover} onMouseLeave={offHover}>
        <PreviewContainer>
          {previewPath.length > 0 ? (
            <Image src={previewPath} size='large' />
          ) : props.path ? (
            <Image src={props.path} size='large' />
          ) : (
            <InlineUploadIcon name='account_circle' size='large' />
          )}
          {hover || dialog ? (
            <FormOverlay onClick={openedDialog}>
              <Dropzone onDrop={onFile} accept={['image/jpeg', 'image/png']}>
                {({
                  getRootProps,
                  getInputProps,
                }: DropzoneState): React.ReactElement => (
                  <DropArea {...getRootProps()}>
                    <Input {...getInputProps()} />
                    {saving ? (
                      <DotLoading small />
                    ) : (
                      <Icon name='camera_enhance' />
                    )}
                  </DropArea>
                )}
              </Dropzone>
            </FormOverlay>
          ) : null}
        </PreviewContainer>
        <div style={{ textAlign: 'center' }}>Add Image</div>
      </div>
      {error ? (
        <div className=''>
          <div className=''>
            <span>
              There was a problem uploading your image. Only .jpg and .png files
              can be uploaded.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export { InlineUploader };

const Input = styled.input`
  &:active {
    outline: none;
  }
  &:focus {
    outline: none;
  }
`;

const DropArea = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  &:active {
    outline: none;
  }
  &:focus {
    outline: none;
  }
`;

const InlineUploadIcon = styled(Icon)`
  height: 130px;
  width: 130px;
  box-sizing: border-box;
  border-radius: 50%;
  background-color: hsla(0, 0%, 100%, 0.8);
  font-size: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewContainer = styled.div`
  width: 130px;
  position: relative;
`;

const FormOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  transition: all 0.5s ease;
  cursor: pointer;
  border-radius: 50%;
`;
